// @tennis-matches/services/tournament-wizard/validation
// Generic validators that can be composed

import { __asStr } from "@tennis-matches/core/utils";
import { isTournamentStartInPast } from "../../tournament/tournamentStartDateTime.js";

/**
 * Normalize name for comparison
 */
function normalizeName(s) {
  return __asStr(s).replace(/\s+/g, " ").toLowerCase();
}

/**
 * Parse court numbers from raw string
 */
function parseCourtNumbers(raw) {
  const s = __asStr(raw);
  if (!s) return [];
  return s
    .split(/[,\s]+/g)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => x.replace(/[^\d]/g, ""))
    .filter(Boolean);
}

/**
 * Generic validators that can be composed
 */
export const validators = {
  /**
   * Validate minimum player count
   */
  minPlayers: (count, min, format = null) => {
    const ok = count >= min;
    const formatName =
      format === "singles" ? "Einzel" : format === "doubles" ? "Doppel" : "";
    const message = !ok
      ? `Mindestens ${min} Spieler erforderlich${formatName ? ` für ${formatName}` : ""}. Aktuell: ${count}`
      : null;
    return { ok, message };
  },

  /**
   * Validate unique player names
   */
  uniqueNames: (names, t = null) => {
    const seen = new Map();
    const nameKeys = Object.keys(names || {})
      .map(Number)
      .sort((a, b) => a - b);

    for (const key of nameKeys) {
      const n = __asStr(names[String(key)]).trim();
      // Skip empty names and "(Optional)" placeholder
      if (n.length > 0 && n !== "(Optional)" && n !== "(optional)") {
        const norm = normalizeName(n);
        if (norm) {
          const arr = seen.get(norm) || [];
          arr.push(key);
          seen.set(norm, arr);
        }
      }
    }

    // Check for duplicates
    const dup = [];
    for (const idxs of seen.values()) {
      if (idxs.length > 1) dup.push(...idxs);
    }

    const ok = dup.length === 0;
    const message = !ok
      ? t
        ? t("settings.validation.namesDuplicate")
        : "Doppelte Spielernamen sind nicht erlaubt."
      : null;

    return { ok, message };
  },

  /**
   * Validate points configuration
   */
  pointsValid: (winPoints, drawPoints, t = null) => {
    const win = Number(winPoints);
    const draw = Number(drawPoints);
    const ok = Number.isFinite(win) && Number.isFinite(draw) && draw < win;
    const message = !ok
      ? t
        ? t("settings.validation.pointsInvalid")
        : "Siegpunkte müssen größer als Unentschieden-Punkte sein"
      : null;
    return { ok, message };
  },

  /**
   * Validate court numbers
   */
  courtsValid: (courtNumbersRaw, courtCount, t = null) => {
    const courtNums = parseCourtNumbers(courtNumbersRaw);
    const used = [];
    for (let i = 0; i < courtCount; i++) {
      used.push(String(courtNums[i] ?? i + 1));
    }
    const badCourt = used.some((x) => !__asStr(x));
    const uniqCourt = new Set(used.filter(Boolean));
    const ok = !badCourt && uniqCourt.size === used.filter(Boolean).length;
    const message = !ok
      ? t
        ? t("settings.validation.courtsInvalid")
        : "Platznummern sind ungültig"
      : null;
    return { ok, message };
  },

  /**
   * Validate time format (HH:MM)
   */
  timeFormat: (timeString) => {
    const s = String(timeString || "").trim();
    const m = s.match(/^(\d{1,2}):(\d{2})$/);
    return {
      ok: m != null,
      message: m == null ? "Ungültiges Zeitformat (erwartet: HH:MM)" : null,
    };
  },

  /**
   * Validate positive number
   */
  positiveNumber: (value, fieldName = "Wert") => {
    const num = Number(value);
    const ok = Number.isFinite(num) && num > 0;
    return {
      ok,
      message: !ok ? `${fieldName} muss größer als 0 sein` : null,
    };
  },

  /**
   * Validate required field
   */
  required: (value, fieldName = "Feld") => {
    const ok = !!__asStr(value);
    return {
      ok,
      message: !ok ? `${fieldName} ist erforderlich` : null,
    };
  },

  /**
   * Validate player count range (warning, not error)
   */
  playerCountRange: (count, min = 4, max = 24) => {
    const ok = count >= min && count <= max;
    return {
      ok, // This is a warning, so ok is true but we return a message
      message: !ok
        ? `Warnung: Die Spieleranzahl ${count} liegt außerhalb des empfohlenen Bereichs (${min}-${max}).`
        : null,
    };
  },

  /**
   * Turnierstart darf nicht in der Vergangenheit liegen.
   */
  startNotInPast: (startDate, startTime, t = null) => {
    const ok = !isTournamentStartInPast(startDate, startTime);
    return {
      ok,
      message: !ok
        ? t
          ? t("matchmixer.setup.validation.startInPast")
          : "Der Turnierstart darf nicht in der Vergangenheit liegen."
        : null,
    };
  },

  /**
   * Validate groupsKo (Clubmeisterschaft): groupCount >= 2, advancePerGroup >= 1,
   * qualifiedTotal <= bracketSize, bracketSize in [4,8,16,32].
   * When tournamentMode === "groupsKo": bracketSize must equal qualifiedTotal (no byes).
   */
  groupsKo: (groupsKo, playerCount, t = null, options = {}) => {
    const { tournamentMode } = options;
    const messages = [];
    const g = groupsKo?.groups ?? {};
    const q = groupsKo?.qualification ?? {};
    const k = groupsKo?.ko ?? {};
    const groupCount = Math.max(0, Math.trunc(Number(g.groupCount) || 0));
    const advancePerGroup = Math.max(
      0,
      Math.trunc(Number(q.advancePerGroup) || 0),
    );
    const includeBestThirds = Math.max(
      0,
      Math.trunc(Number(q.includeBestThirds) || 0),
    );
    const bracketSize = Number(k.bracketSize) || 8;
    const validBracketSizes = [4, 8, 16, 32];

    if (groupCount < 2) {
      messages.push(
        t
          ? t("club.groupsko.validation.groupCountMin")
          : "Mindestens 2 Gruppen erforderlich.",
      );
    }
    if (advancePerGroup < 1) {
      messages.push(
        t
          ? t("club.groupsko.validation.advanceMin")
          : "Mindestens 1 Qualifikant pro Gruppe.",
      );
    }
    if (!validBracketSizes.includes(bracketSize)) {
      messages.push(
        t
          ? t("club.groupsko.validation.bracketSize")
          : "KO-Feldgröße muss 4, 8, 16 oder 32 sein.",
      );
    }
    const qualifiedTotal = groupCount * advancePerGroup + includeBestThirds;
    if (qualifiedTotal > bracketSize) {
      messages.push(
        t
          ? t("club.groupsko.validation.qualifiedExceedsBracket", {
              qualified: qualifiedTotal,
              bracket: bracketSize,
            })
          : `Qualifizierte (${qualifiedTotal}) dürfen KO-Feld (${bracketSize}) nicht überschreiten.`,
      );
    }
    if (tournamentMode === "groupsKo" && bracketSize !== qualifiedTotal) {
      messages.push(
        t
          ? t("club.groupsko.error.bracketMustEqualQualified", {
              qualified: qualifiedTotal,
            })
          : `KO-Feld muss ${qualifiedTotal} Qualifizierte aufnehmen (ohne Freilos).`,
      );
    }
    const playersCount = Math.max(0, Number(playerCount) || 0);
    if (groupCount >= 2 && playersCount > 0) {
      const minPerGroup = Math.floor(playersCount / groupCount);
      if (advancePerGroup > minPerGroup) {
        messages.push(
          t
            ? t("club.groupsko.validation.advancePerGroupTooHigh", {
                max: minPerGroup,
                groupCount,
              })
            : `Weiter pro Gruppe darf maximal ${minPerGroup} bei ${groupCount} Gruppen sein.`,
        );
      }
    }
    return {
      ok: messages.length === 0,
      messages,
    };
  },

  /**
   * Validate pure K.O. mode (Clubmeisterschaft): bracket must match entrant count (no byes).
   */
  koOnly: (koPayload, entrantsCount, t = null) => {
    const messages = [];
    const ko =
      koPayload?.ko && typeof koPayload.ko === "object" ? koPayload.ko : {};
    const bracketSize = Number(ko.bracketSize || 8);
    const validBracketSizes = [4, 8, 16, 32];
    const entrants = Math.max(0, Number(entrantsCount) || 0);
    const isPow2 = (n) => Number.isInteger(n) && n > 0 && (n & (n - 1)) === 0;

    if (!validBracketSizes.includes(bracketSize) || !isPow2(bracketSize)) {
      messages.push(
        t
          ? t("club.groupsko.error.bracketSize")
          : "Bitte eine gültige KO-Feldgröße wählen (4/8/16/32).",
      );
    }
    if (entrants > 0 && bracketSize !== entrants) {
      messages.push(
        t
          ? t("club.ko.error.bracketMustEqualEntrants", { entrants })
          : `Ohne Freilos muss das KO-Feld ${entrants} Teilnehmer aufnehmen.`,
      );
    }
    return {
      ok: messages.length === 0,
      messages,
    };
  },
};
